import { useEffect, useRef, useState } from 'react';
import { writeDriverLocation } from '../../shared/services/userService';

// Minimum ms between Firestore location writes while watching position.
const WRITE_INTERVAL_MS = 15000;

// Mirrors iOS DriverTrackingManager: watches the browser's geolocation and,
// while the driver is online, streams fixes to
// drivers/{driverId}/locations/current + drivers/{driverId}.currentLocation
// via writeDriverLocation. The watcher always runs so the map can show the
// driver's own pin, but Firestore writes only happen while `online` and stop
// on offline/unmount.
export function useDriverLocation(driverId, online, orderId = null) {
  const [position, setPosition] = useState(null); // {lat, lng}
  const [error, setError] = useState(null);
  const lastWriteRef = useRef(0);
  const lastPosRef = useRef(null);
  const orderIdRef = useRef(orderId);
  orderIdRef.current = orderId;

  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setError('Geolocation is not supported by this browser');
      return undefined;
    }
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        // Browser GeolocationPosition — speed/heading/accuracy feed the iOS
        // locations/current contract (iOS writes -1 for invalid values).
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          speed: pos.coords.speed,
          course: pos.coords.heading,
          accuracy: pos.coords.accuracy,
        };
        lastPosRef.current = coords;
        setPosition(coords);
        setError(null);
        if (online && driverId) {
          const now = Date.now();
          if (now - lastWriteRef.current >= WRITE_INTERVAL_MS) {
            lastWriteRef.current = now;
            writeDriverLocation(driverId, coords, orderIdRef.current).catch((e) =>
              console.warn('writeDriverLocation failed:', e)
            );
          }
        }
      },
      (e) => setError(e.message),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [driverId, online]);

  // Push one fix immediately when the driver flips online.
  useEffect(() => {
    if (online && driverId && lastPosRef.current) {
      lastWriteRef.current = Date.now();
      writeDriverLocation(driverId, lastPosRef.current, orderIdRef.current).catch(() => {});
    }
  }, [online, driverId]);

  return { position, error };
}
